import { AxiosError, AxiosResponse } from "axios";

import { getRequest, patchRequest, postRequest } from "@/utils/axios";

function fetchTests(
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return getRequest(
    "/api/v1/user-tests/",
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function fetchTest(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return getRequest(
    `/api/v1/user-tests/${hash}/`,
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

async function fetchTestAsync(hash: string) {
  return getRequest(
    `/api/v1/user-tests/${hash}/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError, response: AxiosResponse) => {
      throw error;
    }
  );
}

async function fetchCurrentTest() {
  const response = await getRequest(
    "/api/v1/user-tests/current/",
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError, response: AxiosResponse) => {
      console.error(error);
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  );
  return response;
}

async function fetchTestInitialResults(hash: string) {
  const response = await getRequest(
    `/api/v1/user-tests/${hash}/initial-results/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError, response: AxiosResponse) => {
      console.error(error);
      throw error;
    }
  );
  return response;
}

function fetchLatestReadyTest(
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return getRequest(
    `/api/v1/user-tests/latest_ready/`,
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function fetchRetestEligibility(
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return getRequest(
    "/api/v1/user-tests/retest-eligibility/",
    onSuccess,
    onError
  );
}

async function asyncFetchLatestReadyTest() {
  const response = await getRequest(
    `/api/v1/user-tests/latest_ready/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError, response: AxiosResponse) => {
      throw error;
    }
  );
  return response;
}

function attachTest(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    "/api/v1/user-tests/",
    { hash: hash },
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function createSnblCheckout(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    `/api/v1/user-tests/${hash}/snbl-checkout/`,
    {},
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function fetchSnblPayment(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return getRequest(
    `/api/v1/user-tests/${hash}/snbl-payment/`,
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function authorizeReleaseToProvider(
  hash: string,
  authorize: boolean,
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    `/api/v1/user-tests/${hash}/provider-release/`,
    {
      provider_release: authorize,
    },
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function submitSample(
  hash: string,
  ph?: string,
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    `/api/v1/user-tests/${hash}/sample/`,
    { ph: ph },
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function submitHealthHistory(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    `/api/v1/user-tests/${hash}/submit/`,
    {},
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function recordConsent(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void,
  finalize?: boolean
) {
  const url = `/api/v1/user-tests/${hash}/consent/${finalize ? "?finalize=true" : ""}`;
  return postRequest(
    url,
    {},
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

// EVY-355: cycleStartDate is YYYY-MM-DD; the backend fires the Braze reminder event
function setCollectionReminder(
  hash: string,
  cycleStartDate: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    `/api/v1/user-tests/${hash}/collection-reminder/`,
    { cycle_start_date: cycleStartDate },
    onSuccess,
    onError
  );
}

function takeSampleLater(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return postRequest(
    `/api/v1/user-tests/${hash}/take-sample-later/`,
    {},
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function fetchAddressInfo(
  hash: string,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return getRequest(
    `/api/v1/lab-order-intake/${hash}/address/`,
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function submitAddressInfo(
  hash: string,
  data: any,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError, response: AxiosResponse) => void
) {
  return patchRequest(
    `/api/v1/lab-order-intake/${hash}/`,
    data,
    (response: AxiosResponse) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response: AxiosResponse) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

const VPCR_ADD_ON = "vPCR-add-on";

async function asyncCreateTestCheckoutURL(testHash: string) {
  const response = await postRequest(
    `/api/v1/user-tests/${testHash}/checkout/`,
    { checkout_type: VPCR_ADD_ON },
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncCreateUngatedRxCheckoutUrl({
  testHash,
  data,
}: {
  testHash: string;
  data: any;
}) {
  const response = await postRequest(
    `/api/v1/user-tests/${testHash}/ungated-checkout/`,
    data,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

const fetchTestProblemsSummary = async (testHash: string) => {
  const response = await getRequest(
    `/api/v1/user-tests/${testHash}/problems-summary/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const fetchTestUngatedRxRecommendations = async (testHash: string) => {
  const response = await getRequest(
    `/api/v1/user-tests/${testHash}/recommended-products/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const fetchTestALaCareRecommendations = async (testHash: string) => {
  const response = await getRequest(
    `/api/v1/user-tests/${testHash}/a-la-care-recommendations/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const fetchTestTransparentCareRecommendations = async (testHash: string) => {
  const response = await getRequest(
    `/api/v1/user-tests/${testHash}/treatment-recommendations/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const fetchTestLightweightTransparentCareRecommendations = async (
  testHash: string
) => {
  const response = await getRequest(
    `/api/v1/user-tests/${testHash}/treatment-recommendations-lightweight/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const fetchUserAllAvailableTreatments = async () => {
  const response = await getRequest(
    `/api/v1/user-tests/all-available-treatments-lightweight/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

async function asyncCreateRetestCheckoutURL({
  discountCode = "RETEST20",
}: {
  discountCode?: string;
}) {
  const requestData: any = {
    discount_code: discountCode,
  };

  const response = await postRequest(
    `/api/v1/user-tests/retest-checkout/`,
    requestData,
    (response: AxiosResponse) => {
      return response.data;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

const fetchTestPdfData = async (testHash: string) => {
  // Encode hash to prevent URL injection and ensure safe interpolation
  const encodedHash = encodeURIComponent(testHash);
  const response = await getRequest(
    `/api/v1/user-tests/${encodedHash}/pdf-data/`,
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

export const testsService = {
  fetchCurrentTest,
  fetchTestInitialResults,
  fetchTests,
  fetchTest,
  fetchTestUngatedRxRecommendations,
  fetchTestALaCareRecommendations,
  fetchTestAsync,
  asyncFetchLatestReadyTest,
  fetchLatestReadyTest,
  fetchRetestEligibility,
  attachTest,
  createSnblCheckout,
  fetchSnblPayment,
  recordConsent,
  takeSampleLater,
  setCollectionReminder,
  submitSample,
  submitHealthHistory,
  fetchAddressInfo,
  fetchTestProblemsSummary,
  submitAddressInfo,
  authorizeReleaseToProvider,
  asyncCreateTestCheckoutURL,
  asyncCreateUngatedRxCheckoutUrl,
  asyncCreateRetestCheckoutURL,
  fetchTestTransparentCareRecommendations,
  fetchTestLightweightTransparentCareRecommendations,
  fetchUserAllAvailableTreatments,
  fetchTestPdfData,
};
